var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import {} from '@sinclair/typebox';
import { Value } from '@sinclair/typebox/value';
import { objectEntries, objectFromEntries } from "./utils.mjs";
import { logger } from "./log.mjs";
let fetch = globalThis.fetch;
export class HttpError extends Error {
    constructor(message, url, status, responseText) {
        super(message);
        this.url = url;
        this.status = status;
        this.responseText = responseText;
    }
}
/**
 * This lets you override the fetch implementation used by the http module.
 * This is useful for testing or if you want to add logging or pass additional headers.
 */
export const overrideHttpFetch = (newFetch) => {
    fetch = newFetch;
};
const fetchWithRefreshToken = (url, requestInit, tokenData) => __awaiter(void 0, void 0, void 0, function* () {
    const makeRequest = (tokenString) => fetch(url, Object.assign(Object.assign({}, requestInit), { headers: Object.assign(Object.assign({}, requestInit.headers), (tokenString
            ? {
                Authorization: `Bearer ${tokenString}`,
            }
            : {})) }));
    if (!tokenData) {
        return makeRequest();
    }
    const firstRequest = yield makeRequest(tokenData.tokenString);
    if (firstRequest.status === 401) {
        const newTokenString = yield tokenData.refreshToken();
        return makeRequest(newTokenString);
    }
    return firstRequest;
});
export const get = (path, baseUrl, schema, token, additionalHeaders) => __awaiter(void 0, void 0, void 0, function* () {
    const url = pathToUrl(path, baseUrl);
    let response;
    try {
        response = yield fetchWithRefreshToken(url, {
            headers: additionalHeaders,
        }, token);
    }
    catch (error) {
        throw new HttpError(`Network connectivity error: ${error.message}`, url, undefined, undefined);
    }
    if (!response.ok) {
        const textResponse = yield response.text();
        throw new HttpError(`HTTP status indicates unsuccessful GET request, response: ${textResponse}`, url, response.status, textResponse);
    }
    return responseToSchema(url, response, schema);
});
export const getWithRetries = (path_1, baseUrl_1, schema_1, token_1, additionalHeaders_1, ...args_1) => __awaiter(void 0, [path_1, baseUrl_1, schema_1, token_1, additionalHeaders_1, ...args_1], void 0, function* (path, baseUrl, schema, token, additionalHeaders, retries = 3) {
    return withRetries(() => get(path, baseUrl, schema, token, additionalHeaders), retries);
});
const withRetries = (action_1, ...args_1) => __awaiter(void 0, [action_1, ...args_1], void 0, function* (action, retries = 3, currentTry = 1) {
    try {
        return yield action();
    }
    catch (error) {
        if (currentTry <= retries && isRetryableHttpError(error)) {
            yield new Promise((resolve) => setTimeout(resolve, currentTry * 1000));
            return withRetries(action, retries, currentTry + 1);
        }
        throw error;
    }
});
const isRetryableHttpError = (error) => {
    // We retry on HttpError that doesn't have a response status as well since that's a local connectivity issue
    // which might be resolved by retrying.
    return error instanceof HttpError && (!(error === null || error === void 0 ? void 0 : error.status) || (error === null || error === void 0 ? void 0 : error.status) === 429 || (error === null || error === void 0 ? void 0 : error.status) > 501);
};
// This function is primarily used to encode URLs in headers to ensure they don't contain invalid characters.
// For example japanese characters in the URL would cause issues if not encoded.
export const encodeHeader = (headerValue) => {
    try {
        // If decodeURIComponent succeeds and gives a different result, it was already encoded
        const decoded = decodeURIComponent(headerValue);
        if (decoded !== headerValue) {
            // Already encoded, return as-is to avoid double-encoding
            return headerValue;
        }
    }
    catch (_a) {
        // decodeURIComponent failed, value contains invalid encoding - treat as unencoded
    }
    // Value is not encoded (or contains invalid encoding), so encode it
    return encodeURIComponent(headerValue);
};
export const post = (path, baseUrl, body, schema, token, additionalHeaders) => __awaiter(void 0, void 0, void 0, function* () {
    const url = pathToUrl(path, baseUrl);
    let response;
    try {
        response = yield fetchWithRefreshToken(url, {
            method: 'POST',
            headers: Object.assign(Object.assign({}, additionalHeaders), { 'Content-Type': 'application/json' }),
            body: JSON.stringify(body),
        }, token);
    }
    catch (error) {
        throw new HttpError(`Network connectivity error: ${error.message}`, url, undefined, undefined);
    }
    if (!response.ok) {
        const textResponse = yield response.text();
        throw new HttpError(`HTTP status indicates unsuccessful POST request, response: ${textResponse}`, url, response.status, textResponse);
    }
    return responseToSchema(url, response, schema);
});
export const postWithRetries = (path_1, baseUrl_1, body_1, schema_1, token_1, additionalHeaders_1, ...args_1) => __awaiter(void 0, [path_1, baseUrl_1, body_1, schema_1, token_1, additionalHeaders_1, ...args_1], void 0, function* (path, baseUrl, body, schema, token, additionalHeaders, retries = 3) {
    return withRetries(() => post(path, baseUrl, body, schema, token, additionalHeaders), retries);
});
const responseToSchema = (url, response, schema) => __awaiter(void 0, void 0, void 0, function* () {
    const contentType = response.headers.get('content-type');
    if ((contentType === null || contentType === void 0 ? void 0 : contentType.includes('application/json')) === false) {
        const textResponse = yield response.text();
        throw new Error(`Unexpected content type in response from ${url}. Expected application/json, got ${contentType}. Response: ${textResponse}`);
    }
    const json = yield response.json();
    if (!Value.Check(schema, json)) {
        const errors = Value.Errors(schema, json);
        logger.error({ errors: Array.from(errors), json, url }, `Unexpected format in HTTP JSON response`);
        const coerced = Value.Cast(schema, json);
        return coerced;
    }
    return json;
});
const pathToUrl = (path, baseUrl) => {
    return new URL(typeof path === 'string' ? path : path.path + queryParamsToString(path.query), baseUrl).toString();
};
const queryParamsToString = (params) => {
    if (!params || !Object.keys(params).length) {
        return '';
    }
    const stringParams = objectFromEntries(objectEntries(params)
        .filter(([_, value]) => value !== undefined)
        .map(([key, value]) => [key, value.toString()]));
    return '?' + new URLSearchParams(stringParams).toString();
};
//# sourceMappingURL=http.js.map